import {
  CircleCheck,
  CircleSlash,
  Flag,
  GitBranch,
  MessageCircleQuestion,
  Play,
  Plus,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Branch, BranchRow, Connector, MergeNote, StepCard, type Tone } from "@/features/flow-kit";
import type { CanvasActions } from "@/features/intake-flow/actions";
import { Board } from "@/features/intake-flow/board";
import { useMediaQuery } from "@/lib/use-media-query";

export type { CanvasActions } from "@/features/intake-flow/actions";
import {
  type FlowEdge,
  type Graph,
  type Problem,
  type Question,
  type Row,
  nodeOf,
  outOf,
  branchRemovable,
  layout,
} from "@/lib/intake-flow";

/**
 * The flow as the admin draws it (ACL-338).
 *
 * A chain of cards top to bottom, with a row of columns wherever it fans out and the Fallback
 * drawn last and dimmed: it is the base flow, and a lead no branch names goes that way. The
 * columns stack under their own headers on a phone.
 *
 * Every card carries a plus on the connector below it, and a dots menu with what can be done to
 * it. A step whose own problem the validator reported wears the error border.
 */

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  question: MessageCircleQuestion,
  paths: GitBranch,
  continue: CircleCheck,
  flag: Flag,
  reject: CircleSlash,
};

function toneOf(kind: string, outcome: string | null): Tone {
  if (kind === "paths") return "branch";
  if (kind === "outcome") return outcome === "flag" ? "flag" : outcome === "reject" ? "reject" : "end";
  return "step";
}

function iconOf(kind: string, outcome: string | null) {
  return ICONS[kind === "outcome" ? (outcome ?? "continue") : kind] ?? MessageCircleQuestion;
}

/**
 * The board on a real screen, the stacked chain on a phone.
 *
 * Two renderers rather than CSS, because they are different shapes rather than the same shape
 * restyled: a board needs a transformed plane and a phone needs ordinary page scroll. The media
 * query keeps only one of them in the DOM, so there is one focus target and one set of menus.
 */
export function Canvas(props: {
  graph: Graph;
  questions: Question[];
  problems: Problem[];
  readOnly: boolean;
  actions: CanvasActions;
  firms?: { id: number; label?: string | null; name: string }[];
  campaigns?: { id: string; name: string }[];
}) {
  const wide = useMediaQuery("(min-width: 768px)");
  const names = React.useMemo(() => {
    const found = new Map<string, string>();
    for (const one of props.firms ?? []) found.set(`firm:${one.id}`, one.label ?? one.name);
    for (const one of props.campaigns ?? []) found.set(`campaign:${one.id}`, one.name);
    return found;
  }, [props.firms, props.campaigns]);
  if (!wide) return <StackedCanvas {...props} />;
  return (
    <Board
      {...props}
      describeBranch={(key, position) => {
        const edge = outOf(props.graph, key).find((e) => e.position === position);
        if (!edge) return "";
        if (!edge.conditions.length) return "Everyone the paths above did not take.";
        return `Only if ${describeClauses(edge.conditions, names)}`;
      }}
    />
  );
}

function StackedCanvas({
  graph,
  questions,
  problems,
  readOnly,
  actions,
}: {
  graph: Graph;
  questions: Question[];
  problems: Problem[];
  readOnly: boolean;
  actions: CanvasActions;
}) {
  const rows = layout(graph);
  const trouble = new Set(problems.map((p) => p.key).filter(Boolean) as string[]);

  if (!graph.start) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-3">
        <StepCard icon={Play} tone="start" title="The intake starts here">
          <p>Nothing is asked yet.</p>
        </StepCard>
        {readOnly ? null : (
          <Connector>
            <PlusButton onClick={() => actions.onAdd(null)} />
          </Connector>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-3">
      <StepCard icon={Play} tone="start" title="The intake starts here">
        <p>Every lead begins at the first question below.</p>
      </StepCard>
      <Rows
        rows={rows}
        graph={graph}
        questions={questions}
        trouble={trouble}
        readOnly={readOnly}
        actions={actions}
      />
    </div>
  );
}

/**
 * One level of the canvas, and every level under it.
 *
 * A branch's own rows are drawn by this same component, so a split inside a branch draws its own
 * bus and columns exactly as the trunk does (ACL-338). `layout` caps how deep that goes.
 */
function Rows({
  rows,
  graph,
  questions,
  trouble,
  readOnly,
  actions,
}: {
  rows: Row[];
  graph: Graph;
  questions: Question[];
  trouble: Set<string>;
  readOnly: boolean;
  actions: CanvasActions;
}) {
  return (
    <>
      {rows.map((row) => {
        const node = nodeOf(graph, row.key);
        if (!node) return null;
        if (row.kind === "step") {
          const onward = outOf(graph, row.key)[0] ?? null;
          return (
            <React.Fragment key={row.key}>
              <Connector>
                {readOnly ? null : (
                  <PlusButton
                    onClick={() => actions.onAdd(onward ? { edge: onward } : { after: row.key })}
                  />
                )}
              </Connector>
              <Step
                graph={graph}
                nodeKey={row.key}
                questions={questions}
                invalid={trouble.has(row.key)}
                readOnly={readOnly}
                actions={actions}
              />
            </React.Fragment>
          );
        }
        const merge = row.merge ? nodeOf(graph, row.merge) : null;
        return (
          <React.Fragment key={row.key}>
            <Connector>
              {readOnly ? null : (
                <PlusButton onClick={() => actions.onAdd({ after: row.key })} />
              )}
            </Connector>
            <Step
              graph={graph}
              nodeKey={row.key}
              questions={questions}
              invalid={trouble.has(row.key)}
              readOnly={readOnly}
              actions={actions}
            />
            <BranchRow>
              {(row.branches ?? []).map((branch, index) => {
                const fallback = branch.conditions.length === 0;
                return (
                  <Branch
                    key={branch.position}
                    label={branch.label ?? (fallback ? "Everyone else" : `Branch ${index + 1}`)}
                    index={index}
                    total={row.branches?.length ?? 1}
                    fallback={fallback}
                  >
                    <ConditionCard
                      graph={graph}
                      nodeKey={row.key}
                      position={branch.position}
                      fallback={fallback}
                      readOnly={readOnly}
                      actions={actions}
                    />
                    {/* An empty branch still needs a way onto it, or a path nobody has built on
                        yet is a dead end on the canvas. */}
                    {branch.rows.length === 0 && !readOnly ? (
                      <Connector>
                        <PlusButton
                          onClick={() =>
                            actions.onAdd(
                              (() => {
                                const on = outOf(graph, row.key).find(
                                  (e) => e.position === branch.position,
                                );
                                return on ? { edge: on } : null;
                              })(),
                            )
                          }
                        />
                      </Connector>
                    ) : null}
                    <Rows
                      rows={branch.rows}
                      graph={graph}
                      questions={questions}
                      trouble={trouble}
                      readOnly={readOnly}
                      actions={actions}
                    />
                  </Branch>
                );
              })}
            </BranchRow>
            {readOnly ? null : (
              <div className="flex justify-center">
                <Button variant="ghost" size="sm" onClick={() => actions.onAddBranch(row.key)}>
                  Add a branch
                </Button>
              </div>
            )}
            {merge ? <MergeNote title={merge.title} /> : null}
          </React.Fragment>
        );
      })}
    </>
  );
}

function PlusButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Add a step here"
      className="my-1 grid size-6 place-items-center rounded-full border border-border bg-surface-sunken text-text-muted hover:bg-surface-hover"
    >
      <Plus className="size-3.5" />
    </button>
  );
}

function Step({
  graph,
  nodeKey,
  questions,
  invalid,
  readOnly,
  actions,
}: {
  graph: Graph;
  nodeKey: string;
  questions: Question[];
  invalid: boolean;
  readOnly: boolean;
  actions: CanvasActions;
}) {
  const node = nodeOf(graph, nodeKey);
  if (!node) return null;
  const question = questions.find((q) => q.key === node.field_key);
  const branches = outOf(graph, nodeKey).length;
  return (
    <StepCard
      icon={iconOf(node.kind, node.outcome)}
      tone={toneOf(node.kind, node.outcome)}
      title={node.title}
      invalid={invalid}
      actions={
        readOnly ? null : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" aria-label={`What to do with ${node.title}`}>
                •••
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => actions.onEdit(nodeKey)}>Edit</DropdownMenuItem>
              {node.kind === "question" && branches <= 1 && question?.can_branch ? (
                <DropdownMenuItem onSelect={() => actions.onBranchOnAnswer(nodeKey)}>
                  Make a path per answer
                </DropdownMenuItem>
              ) : null}
              {/* A Paths step that has not fanned out yet is otherwise a dead end: its only way
                  on is to pick what it varies by. */}
              {node.kind === "paths" ? (
                <DropdownMenuItem onSelect={() => actions.onVaryBy(nodeKey)}>
                  {branches > 1 ? "Pick other firms or campaigns" : "Pick firms or campaigns"}
                </DropdownMenuItem>
              ) : null}
              {branches > 1 ? (
                <DropdownMenuItem onSelect={() => actions.onAddBranch(nodeKey)}>
                  Add another path
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem destructive onSelect={() => actions.onDelete(nodeKey)}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      }
    >
      {node.kind === "question" ? (
        <p className="text-xs">
          {question ? question.title : "Pick a question"}
          {node.config?.must_answer === false ? " · can be skipped" : ""}
          {branches > 1 ? ` · ${branches} paths below` : ""}
        </p>
      ) : null}
      {node.kind === "paths" && branches <= 1 ? (
        <p className="text-xs">
          Nothing to vary by yet. Pick the firms or campaigns from this card's menu.
        </p>
      ) : null}
      {node.kind === "outcome" && typeof node.config?.reason === "string" ? (
        <p className="text-xs">{node.config.reason}</p>
      ) : null}
    </StepCard>
  );
}

function ConditionCard({
  graph,
  nodeKey,
  position,
  fallback,
  readOnly,
  actions,
}: {
  graph: Graph;
  nodeKey: string;
  position: number;
  fallback: boolean;
  readOnly: boolean;
  actions: CanvasActions;
}) {
  const edge = outOf(graph, nodeKey).find((e) => e.position === position);
  if (!edge) return null;
  const words = fallback
    ? "No conditions: everyone no other branch named comes this way."
    : describeClauses(edge.conditions);
  return (
    <div className="rounded-lg border border-dashed border-border bg-surface-sunken/60 p-3 text-xs text-text-muted">
      <p className="font-medium text-text">{fallback ? "Everyone else" : "Only continue if"}</p>
      <p className="mt-1">{words || "This branch has no conditions yet."}</p>
      {fallback ? (
        <p className="mt-1">
          The last path always matches, so nobody reaches a question with nowhere to go. To ask
          this group something different, give the paths above it their own conditions. To remove
          the paths altogether, delete the step that made them.
        </p>
      ) : null}
      {readOnly ? null : (
        <div className="mt-2 flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => actions.onBranchConditions(nodeKey, position)}>
            {fallback ? "Rename" : "Edit"}
          </Button>
          {/* The last path is never removed on its own: it is where every lead the others did
              not name goes. Removing the paths means deleting the step that made them, which the
              card says in words rather than leaving a dead button on the screen. */}
          {branchRemovable(graph, nodeKey, position) ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => actions.onRemoveBranch(nodeKey, position)}
            >
              Remove
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** One clause, as a sentence. Keys, never a stored value. */
/**
 * What a whole rule says, in words, with its joins.
 *
 * `and` binds tighter than `or`, so the clauses are read as or-separated groups. A group of more
 * than one is bracketed, because "A or B and C" on a card is ambiguous to a reader even though
 * it is not to the engine.
 */
export function describeClauses(
  clauses: { source: string; field?: string; op: string; value?: unknown; join?: string }[],
  names?: Map<string, string>,
): string {
  const groups: string[][] = [[]];
  for (const [n, clause] of clauses.entries()) {
    if (n && clause.join === "or") groups.push([]);
    groups[groups.length - 1]!.push(describeClause(clause, names));
  }
  const filled = groups.filter((one) => one.length);
  // Brackets only earn their place once there is an `or` to be ambiguous against.
  const said = filled.map((one) =>
    one.length > 1 && filled.length > 1 ? `(${one.join(" and ")})` : one.join(" and "),
  );
  return said.join(" or ");
}

/** What a rule says, in words. `names` turns a firm or campaign id into the name people know. */
export function describeClause(
  clause: { source: string; field?: string; op: string; value?: unknown },
  names?: Map<string, string>,
): string {
  const what =
    clause.source === "answer"
      ? (clause.field ?? "the answer")
      : clause.source === "firm"
        ? "the firm"
        : clause.source === "campaign"
          ? "the campaign"
          : clause.source;
  // A rule stores an id, which is what the engine compares; on the canvas it has to read as the
  // name the admin picked, or a path says "firm is 7".
  const named = (one: unknown) => names?.get(`${clause.source}:${String(one)}`) ?? String(one ?? "");
  const value = Array.isArray(clause.value)
    ? clause.value.map(named).join(", ")
    : named(clause.value);
  switch (clause.op) {
    case "eq":
      return `${what} is ${value}`;
    case "ne":
      return `${what} is not ${value}`;
    case "in":
      return `${what} is one of ${value}`;
    case "not_in":
      return `${what} is none of ${value}`;
    case "contains":
      return `${what} includes ${value}`;
    case "contains_any":
      return `${what} includes any of ${value}`;
    case "lt":
      return `${what} is under ${value}`;
    case "gt":
      return `${what} is over ${value}`;
    case "empty":
      return `${what} was not answered`;
    case "not_empty":
      return `${what} was answered`;
    default:
      return `${what} ${clause.op} ${value}`;
  }
}
